import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  useParams,
  useNavigate,
  useLocation,
  Navigate,
} from "react-router-dom";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronUpIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";

import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import { usePermissionValidator } from "../../api/UserPermissionValidators";
import Button, {
  ButtonColorTypes,
  ButtonTypes,
} from "../../shared-components/Button";
import PDFDownloadWrapper from "../../reports/PDFDownloadWrapper";
import PersonInfoLabel, { PersonInfoLabelCustom } from "./PersonInfoLabel";
import ConsentFormSection from "../ConsentFormSection";
import Summary360Body from "./Summary360Body";
import Summary360MenuItems from "./Summary360MenuItems";
import { useSummary360Data } from "./useSummary360Data";
import Summary360RerunBanner from "./Summary360RerunBanner";
import Summary360CompanyBanner from "./Summary360CompanyBanner";
import { useGetCompanyBase } from "../../api/CompaniesApi";
import { useReportReruns } from "../../api/ReportsApi";
import { isRerunReport } from "../../reports/reportRerunUtils";
import { formattedDate } from "../../utils";
import { NameType } from "../../api/Person";
import LoadingSpinner from "../../shared-components/LoadingSpinner";
import CopyIcon from "../../shared-components/CopyIcon";
import Summary360PrintReportHeader from "./Summary360PrintReportHeader";
import ViewReportLogsButton from "../../shared-components/ViewReportLogsButton";
import EuRegionBadge from "../../shared-components/EuRegionBadge";
import { formatEmploymentEntry } from "../utils/personInfoFormat";
import { ThreeSixtyItemTypes } from "../../api/Items";

const Summary360: React.FC = () => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [additionalPersonInfoExpanded, setAdditionalPersonInfoExpanded] =
    useState(false);

  const {
    canPrintCurrentView,
    canDownloadPDF,
    canViewAllReportsWithinCompany,
    canAddNotes,
  } = usePermissionValidator();

  const {
    isLoading,
    reportData,
    reportDataError,
    personEx,
    summaryItems,
    summaryItemsLoading,
    personDocs,
    personConsentForms,
    personExLoading,
    reportDataLoading,
    blueprint,
  } = useSummary360Data(params.id, canViewAllReportsWithinCompany);

  const { reportReruns } = useReportReruns(params.id);

  const companyUUID = reportData?.company_uuid;

  const {
    data: company,
    mutate: getCompanyBase,
    isLoading: companyLoading,
    variables: fetchedCompanyUUID,
  } = useGetCompanyBase();

  useEffect(() => {
    if (companyUUID) {
      getCompanyBase(companyUUID);
    }
  }, [companyUUID, getCompanyBase]);

  const companyMatchesReport =
    !!companyUUID && fetchedCompanyUUID === companyUUID && !companyLoading;

  const showRerunBanner = useMemo(
    () =>
      reportData?.report_uuid === params.id &&
      isRerunReport(reportData, reportReruns),
    [reportData, params.id, reportReruns]
  );

  const blueprintItemTypes = blueprint?.item_types;
  const hasFamaReport = blueprintItemTypes?.length
    ? blueprintItemTypes.some((t) => t.name === ThreeSixtyItemTypes.FAMA_REPORT)
    : true;

  const isReportForbidden = (reportDataError as any)?.response?.status === 403;

  const consentDocs =
    personDocs?.filter((doc) => doc.type === "consent-form") ?? [];

  function print() {
    if (window.zE) {
      window.zE("messenger", "hide");
    }
    // Set up an event listener to show the widget again after printing
    window.addEventListener("afterprint", () => {
      if (window.zE) {
        window.zE("messenger", "show");
      }
    });

    setTimeout(() => {
      window.print();
    }, 1000);
  }

  const handleBack = useCallback(() => {
    const originUrl = (location.state as { originUrl?: string } | null)
      ?.originUrl;
    if (originUrl) {
      navigate(originUrl);
      return;
    }
    navigate("/360/finished");
  }, [navigate, location.state]);

  const handleViewReportLogs = useCallback(() => {
    if (!reportData?.report_uuid) return;
    const returnTo = encodeURIComponent(
      `/360/${reportData.report_uuid}/summary`
    );
    const base = `${window.location.origin}${window.location.pathname}`;
    window.open(
      `${base}#/reports/${reportData.report_uuid}/logs?returnTo=${returnTo}`,
      "_blank",
      "noopener,noreferrer"
    );
  }, [reportData?.report_uuid]);

  const formattedFinishDate = formattedDate(reportData?.finish_at ?? "");
  const formattedBirthDate = formattedDate(personEx?.birth_date ?? "");

  if (isReportForbidden) {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <div>
      {/* Header */}
      <div className="print:hidden">
        <div>
          <button
            onClick={handleBack}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-2 text-lg font-medium"
          >
            <ChevronLeftIcon className="w-5 h-5 mr-2" />
            Back to List
          </button>
        </div>

        <div className="flex justify-between">
          <div className="text-4xl font-bold text-gray-900">
            360 Report Summary
          </div>
          <div className="flex justify-center gap-2">
            <div className="flex gap-2 h-10">
              {canPrintCurrentView && (
                <Button
                  type={ButtonTypes.print}
                  text="Download this View"
                  colorType={ButtonColorTypes.outline}
                  onClick={print}
                  disabled={isLoading}
                />
              )}

              {canDownloadPDF && (
                <PDFDownloadWrapper report_uuid={params.id ?? ""}>
                  <Button
                    type={ButtonTypes.default}
                    text="Download Report"
                    colorType={ButtonColorTypes.success}
                    onClick={() => {}}
                    disabled={isLoading}
                    className="h-10"
                  />
                </PDFDownloadWrapper>
              )}
            </div>
            <div>
              <div className="flex items-center">
                {!reportDataLoading && reportData && (
                  <Summary360MenuItems
                    reportUUID={reportData.report_uuid}
                    report={reportData}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
        {showRerunBanner && reportData && (
          <Summary360RerunBanner
            report={reportData}
            reportReruns={reportReruns}
          />
        )}
      </div>

      {/* Person Info */}
      <div className="print:hidden bg-white rounded-md py-6 px-6 mt-6 mb-6 w-full">
        <div className="px-4">
          <div className="flex items-center gap-2 border-b-[1px] pb-2 border-gray-200">
            <UserCircleIcon className="w-6 h-6 text-gray-500" />
            <div className="text-2xl font-bold text-gray-800">Person Info</div>
          </div>
          <div>
            {(personExLoading || reportDataLoading) && <LoadingSpinner />}
            {!personExLoading && !reportDataLoading && (
              <div className="flex py-6 gap-4 flex-wrap">
                <div className="flex flex-wrap gap-y-4 gap-x-2 flex-1 min-w-0">
                  <PersonInfoLabelCustom title="Name">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-gray-500 overflow-hidden text-ellipsis whitespace-nowrap">
                        {reportData?.full_name ?? ""}
                      </span>
                      <EuRegionBadge personUuid={reportData?.person_uuid} />
                    </div>
                  </PersonInfoLabelCustom>
                  <PersonInfoLabel
                    title="Company"
                    values={[reportData?.company_name ?? ""]}
                  />

                  <PersonInfoLabel
                    title="Requester"
                    values={[reportData?.requested_by_name ?? "None"]}
                  />
                  <PersonInfoLabel
                    title="Check Type"
                    values={[reportData?.blueprint_name ?? ""]}
                  />
                  <PersonInfoLabel
                    title="Behavior Kit"
                    values={[reportData?.behavior_kit_name ?? "Default"]}
                  />
                  <PersonInfoLabelCustom title="Report Id">
                    <div className="flex items-center gap-2 text-gray-500 overflow-hidden text-ellipsis whitespace-nowrap">
                      {!!reportData && (
                        <>
                          {reportData.report_uuid.substring(0, 8)}...
                          <CopyIcon
                            textToCopy={reportData?.report_uuid ?? ""}
                          />
                        </>
                      )}
                      {reportDataLoading && <LoadingSpinner />}
                      {!reportDataLoading && !reportData && (
                        <span className="text-gray-500">None</span>
                      )}
                    </div>
                  </PersonInfoLabelCustom>
                  <PersonInfoLabel
                    title="Assignment Number"
                    values={
                      reportData?.client_report_id?.toString()
                        ? [reportData?.client_report_id?.toString()]
                        : []
                    }
                    placeholder="Not Provided"
                  />
                  <PersonInfoLabel
                    title="Lookback Period"
                    values={[
                      `${blueprint?.lookback} ${
                        blueprint?.lookback_months === 1 ? "Month" : "Months"
                      }`,
                    ]}
                    placeholder="Not Provided"
                  />
                  <PersonInfoLabel
                    title="Completed"
                    values={[formattedFinishDate]}
                  />
                </div>
                {reportData && (
                  <div className="flex-shrink-0 w-48 self-start">
                    <ViewReportLogsButton
                      onClick={handleViewReportLogs}
                      canAddNotes={canAddNotes}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="mt-4 rounded-md border border-gray-200 bg-gray-50 overflow-hidden">
          <button
            type="button"
            onClick={() => setAdditionalPersonInfoExpanded((prev) => !prev)}
            className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-gray-100 transition-colors"
            aria-expanded={additionalPersonInfoExpanded}
          >
            <span className="text-lg font-semibold text-gray-800">
              Additional Person Info
            </span>
            <span className="flex-shrink-0 bg-ourGreen h-6 w-6 text-white rounded-sm ">
              {additionalPersonInfoExpanded ? (
                <ChevronUpIcon className="h-6 w-6" aria-hidden />
              ) : (
                <ChevronDownIcon className="h-6 w-6" aria-hidden />
              )}
            </span>
          </button>
          <div
            className="grid transition-[grid-template-rows] duration-200 ease-in-out"
            style={{
              gridTemplateRows: additionalPersonInfoExpanded ? "1fr" : "0fr",
            }}
          >
            <div className="min-h-0 overflow-hidden">
              <div className="px-4 pb-4 pt-1 flex flex-wrap gap-y-4 gap-x-2">
                {personExLoading && <LoadingSpinner />}
                {!personExLoading && (
                  <>
                    <PersonInfoLabel
                      title="Date of Birth"
                      values={[
                        formattedBirthDate === "NA"
                          ? "Not Provided"
                          : formattedBirthDate,
                      ]}
                      placeholder="Not Provided"
                    />
                    <PersonInfoLabel
                      title="Phone"
                      values={
                        personEx?.phones.map((phone) => phone.raw_number) ?? []
                      }
                      placeholder="Not Provided"
                    />
                    <PersonInfoLabel
                      title="Education"
                      values={
                        personEx?.degrees.map((degree) => degree.name) ?? []
                      }
                      placeholder="Not Provided"
                    />
                    <PersonInfoLabel
                      title="Resume"
                      values={
                        personDocs
                          ?.filter((doc) => doc.type === "resume")
                          .map((doc) => ({
                            value: doc.filename,
                            link: doc.url,
                          })) ?? []
                      }
                      placeholder="Not Provided"
                    />
                    <PersonInfoLabel
                      title="Aliases"
                      values={
                        personEx?.names
                          .filter((name) => name.name_type !== NameType.MAIN)
                          .map(
                            (name) => name.first_name + " " + name.last_name
                          ) ?? []
                      }
                      placeholder="None"
                    />
                    <PersonInfoLabel
                      title="Location"
                      values={
                        personEx?.addresses.map(
                          (address) =>
                            address.line1 +
                            " " +
                            address.city +
                            " " +
                            address.state +
                            " " +
                            address.zip +
                            " " +
                            address.country
                        ) ?? []
                      }
                      placeholder="Not Provided"
                    />
                    <PersonInfoLabel
                      title="Certificates"
                      values={
                        personDocs
                          ?.filter((doc) => doc.type === "other")
                          .map((doc) => ({
                            value: doc.filename,
                            link: doc.url,
                          })) ?? []
                      }
                      placeholder="Not Provided"
                    />
                    <div className="flex flex-col w-[17rem]">
                      <ConsentFormSection
                        consentDocs={consentDocs}
                        consentForms={personConsentForms ?? []}
                        variant="summary"
                      />
                    </div>
                    <PersonInfoLabel
                      title="Email"
                      values={
                        personEx?.emails.map((email) => email.address) ?? []
                      }
                      placeholder="Not Provided"
                    />
                    <PersonInfoLabel
                      title="Employment"
                      values={
                        personEx?.jobs
                          .map(formatEmploymentEntry)
                          .filter(Boolean) ?? []
                      }
                      placeholder="Not Provided"
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {reportData && <Summary360PrintReportHeader reportSummary={reportData} />}

      {companyMatchesReport && company?.banner_notes && (
        <Summary360CompanyBanner text={company.banner_notes} />
      )}

      {reportData && (
        <Summary360Body
          summaryItems={summaryItems || []}
          isLoading={summaryItemsLoading}
          personEx={personEx}
          hasFamaReport={hasFamaReport}
        />
      )}
    </div>
  );
};

export default Summary360;
